import rtokens from '@reserve-protocol/rtokens'
import { GraphQLClient } from 'graphql-request'
import { atom } from 'jotai'
import { getCurrentTime } from 'utils'
import { AvailableChain, ChainId, defaultChain } from 'utils/chains'
import { blockDuration } from 'utils/constants'
import { formatEther, Address } from 'viem'
/**
 * #########################
 * Chain state related atoms
 * #########################
 */
export const chainIdAtom = atom<AvailableChain>(defaultChain)
export const blockAtom = atom<number | undefined>(undefined)
export const blockTimestampAtom = atom<number>(getCurrentTime())
// Acts as an application timer, basically it gets updated every minute which is a good debounce metric
export const timestampAtom = atom<number>(getCurrentTime())
export const debouncedBlockAtom = atom<number | undefined>(undefined)

export const walletAtom = atom<Address | null>(null)
export const walletChainAtom = atom<number | undefined>(undefined)
export const isWalletInvalidAtom = atom((get) => {
  const chainId = get(chainIdAtom)
  const walletChain = get(walletChainAtom)

  if (!walletChain) {
    return false
  }

  return chainId !== walletChain
})

export const rTokenListAtom = atom((get) => {
  const chainId = get(chainIdAtom)

  return rtokens[chainId] ?? {}
})

export const allrTokenListAtom = atom((get) => {
  const ethereumTokens = rtokens[ChainId.Mainnet]
  const baseTokens = rtokens[ChainId.Base]
  const arbitrumTokens = rtokens[ChainId.Arbitrum]

  return Object.fromEntries([
    ...Object.values(ethereumTokens).map((i) => [
      i.address,
      { ...i, chainId: ChainId.Mainnet },
    ]),
    ...Object.values(baseTokens).map((i) => [
      i.address,
      { ...i, chainId: ChainId.Base },
    ]),
    ...Object.values(arbitrumTokens).map((i) => [
      i.address,
      { ...i, chainId: ChainId.Arbitrum },
    ]),
  ]) as Record<string, (typeof ethereumTokens)[string] & { chainId: number }>
})

export const secondsPerBlockAtom = atom((get) => {
  const chainId = get(chainIdAtom)

  return blockDuration[chainId] || 12
})

/**
 * ##################
 * Price related atom
 * ##################
 */

// TODO: ETH initial null state
export const ethPriceAtom = atom(1)
export const btcPriceAtom = atom<number | null>(null)
export const gasFeeAtom = atom<bigint | null>(null)
export const gasPriceAtom = atom(
  (get) => Number(formatEther(get(gasFeeAtom) || 0n)) * get(ethPriceAtom)
)

export const SUBGRAPH_URL = {
  [ChainId.Mainnet]:
    'https://api.goldsky.com/api/public/project_cmgzim3e100095np2gjnbh6ry/subgraphs/dtf-yield-mainnet/4.2.0-v2/gn',
  [ChainId.Base]:
    'https://api.goldsky.com/api/public/project_cmgzim3e100095np2gjnbh6ry/subgraphs/dtf-yield-base/4.2.0-v2/gn',
  [ChainId.Arbitrum]:
    'https://api.goldsky.com/api/public/project_cmgzim3e100095np2gjnbh6ry/subgraphs/reserve-arbitrum/prod/gn',
  [ChainId.BSC]:
    'https://api.goldsky.com/api/public/project_cmgzim3e100095np2gjnbh6ry/subgraphs/reserve-arbitrum/prod/gn', // TODO? maybe never
}

export const INDEX_DTF_SUBGRAPH_URL = {
  [ChainId.Mainnet]:
    'https://api.goldsky.com/api/public/project_cmgzim3e100095np2gjnbh6ry/subgraphs/dtf-index-mainnet/prod/gn',
  [ChainId.Base]:
    'https://api.goldsky.com/api/public/project_cmgzim3e100095np2gjnbh6ry/subgraphs/dtf-index-base/prod/gn',
  [ChainId.Arbitrum]:
    'https://api.goldsky.com/api/public/project_cmgzim3e100095np2gjnbh6ry/subgraphs/dtf-index-bsc/prod/gn', // TODO? maybe never
  [ChainId.BSC]:
    'https://api.goldsky.com/api/public/project_cmgzim3e100095np2gjnbh6ry/subgraphs/dtf-index-bsc/prod/gn',
}

// TODO: Multi fork network graph
export const GRAPH_CLIENTS = {
  [ChainId.Mainnet]: new GraphQLClient(
    import.meta.env.VITE_SUBGRAPH_URL || SUBGRAPH_URL[ChainId.Mainnet]
  ),
  [ChainId.Base]: new GraphQLClient(SUBGRAPH_URL[ChainId.Base]),
  [ChainId.Arbitrum]: new GraphQLClient(SUBGRAPH_URL[ChainId.Arbitrum]),
}

export const INDEX_GRAPH_CLIENTS = {
  [ChainId.Mainnet]: new GraphQLClient(INDEX_DTF_SUBGRAPH_URL[ChainId.Mainnet]),
  [ChainId.Base]: new GraphQLClient(INDEX_DTF_SUBGRAPH_URL[ChainId.Base]),
  [ChainId.BSC]: new GraphQLClient(INDEX_DTF_SUBGRAPH_URL[ChainId.BSC]),
}

export const gqlClientAtom = atom(
  (get) => GRAPH_CLIENTS[get(chainIdAtom)] || GRAPH_CLIENTS[ChainId.Mainnet]
)

/**
 * #########################
 * Dev mode related atoms
 * #########################
 */
export const devModeAtom = atom(false)
